/**
 * DescribeTab Component
 *
 * Tab for generating prompts from existing images using Fooocus describe feature.
 * Analyzes an image and returns a descriptive prompt and tags.
 */

import { useState } from 'react';
import { FileImage, Loader2, Copy, Check, ArrowRight, AlertCircle } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/hooks/useToast';
import { ImageUploader } from '../ImageUploader';
import { imageGenerationAPI } from '@/services/imageGenerationAPI';
import type { DescribeType, DescribeResponse } from '@/types';
import { TIMEOUTS } from '@/constants/timings';

import { handleError } from '@/utils/errorHandler';

interface DescribeTabProps {
  onUsePrompt?: (prompt: string) => void;
}

export function DescribeTab({ onUsePrompt }: DescribeTabProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [sourceImage, setSourceImage] = useState<string>('');
  const [describeType, setDescribeType] = useState<DescribeType>('Photograph');
  const [isDescribing, setIsDescribing] = useState(false);
  const [result, setResult] = useState<DescribeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleImageSelect = (base64: string) => {
    setSourceImage(base64);
    setResult(null);
    setError(null);
  };

  const handleImageRemove = () => {
    setSourceImage('');
    setResult(null);
    setError(null);
  };

  const handleDescribe = async () => {
    if (!sourceImage) {
      showToast('error', 'Error', 'Please upload an image first');
      return;
    }

    setIsDescribing(true);
    setResult(null);
    setError(null);

    try {
      const response = await imageGenerationAPI.describeImage({
        image: sourceImage,
        describe_type: describeType,
      });

      if (response.success && response.data) {
        setResult(response.data);
        showToast(
          'success',
          t('image.describe.result.title', { defaultValue: 'Generated Prompt' }),
          t('image.describe.result.processingTime', {
            defaultValue: 'Analyzed in {{time}} seconds',
            time: response.data.processing_time_seconds?.toFixed(1) || '0',
          })
        );
      } else {
        const errorMsg = response.error || 'Failed to describe image';
        setError(errorMsg);
        showToast('error', 'Error', errorMsg);
      }
    } catch (err) {
      const errorMessage = handleError(err, 'DescribeTab', 'Unknown error');
      setError(errorMessage);
      showToast('error', 'Error', errorMessage);
    } finally {
      setIsDescribing(false);
    }
  };

  const handleCopyPrompt = async () => {
    if (!result?.prompt) return;

    try {
      await navigator.clipboard.writeText(result.prompt);
      setCopied(true);
      showToast('success', t('image.describe.result.copied', { defaultValue: 'Copied!' }), '');
      setTimeout(() => setCopied(false), TIMEOUTS.COPY_FEEDBACK);
    } catch {
      showToast('error', 'Error', 'Failed to copy to clipboard');
    }
  };

  const handleUsePrompt = () => {
    if (result?.prompt && onUsePrompt) {
      onUsePrompt(result.prompt);
      showToast('success', 'Prompt Applied', 'Switched to Text to Image tab with generated prompt');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start space-x-2">
        <FileImage className="h-5 w-5 text-blue-600 mt-0.5" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-secondary-100">
            {t('image.describe.title', { defaultValue: 'Describe' })}
          </h3>
          <p className="text-sm text-gray-600 dark:text-secondary-400">
            {t('image.describe.description', {
              defaultValue: 'Generate a prompt from an existing image',
            })}
          </p>
        </div>
      </div>

      {/* Image Upload */}
      <div>
        <label className="label-gray-alt">
          {t('image.describe.uploadPrompt', { defaultValue: 'Upload an image to analyze' })}
        </label>
        <ImageUploader onImageSelect={handleImageSelect} onRemove={handleImageRemove} />
      </div>

      {/* Type Selection */}
      {sourceImage && (
        <div>
          <label className="label-gray-alt">
            {t('image.describe.type', { defaultValue: 'Content Type' })}
          </label>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setDescribeType('Photograph')}
              className={`p-4 rounded-lg border-2 transition-all ${
                describeType === 'Photograph'
                  ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-900 dark:text-blue-200'
                  : 'border-gray-200 dark:border-secondary-600 bg-white dark:bg-secondary-800 text-gray-700 dark:text-secondary-200 hover:border-gray-300 dark:hover:border-secondary-500'
              }`}
            >
              <div className="font-medium">
                {t('image.describe.types.Photograph', { defaultValue: 'Photograph' })}
              </div>
              <div className="text-xs mt-1 opacity-75">
                {t('image.describe.typeDescriptions.Photograph', {
                  defaultValue: 'For realistic photos',
                })}
              </div>
            </button>
            <button
              onClick={() => setDescribeType('Art')}
              className={`p-4 rounded-lg border-2 transition-all ${
                describeType === 'Art'
                  ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-900 dark:text-blue-200'
                  : 'border-gray-200 dark:border-secondary-600 bg-white dark:bg-secondary-800 text-gray-700 dark:text-secondary-200 hover:border-gray-300 dark:hover:border-secondary-500'
              }`}
            >
              <div className="font-medium">
                {t('image.describe.types.Art', { defaultValue: 'Art / Anime' })}
              </div>
              <div className="text-xs mt-1 opacity-75">
                {t('image.describe.typeDescriptions.Art', {
                  defaultValue: 'For artwork, illustrations, anime',
                })}
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Describe Button */}
      {sourceImage && (
        <button onClick={handleDescribe} disabled={isDescribing} className="btn-primary-full">
          {isDescribing ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>{t('image.describe.describing', { defaultValue: 'Analyzing...' })}</span>
            </>
          ) : (
            <span>{t('image.describe.describe', { defaultValue: 'Describe Image' })}</span>
          )}
        </button>
      )}

      {/* Inline Error Display */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-300">
              {t('image.describe.error.title', { defaultValue: 'Failed to describe image' })}
            </p>
            <p className="text-sm text-red-600 dark:text-red-400 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Result Display */}
      {result && (
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-6 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-900 dark:text-secondary-100">
                {t('image.describe.result.title', { defaultValue: 'Generated Prompt' })}
              </h4>
              <button
                onClick={handleCopyPrompt}
                className="flex items-center space-x-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4" />
                    <span>{t('image.describe.result.copied', { defaultValue: 'Copied!' })}</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    <span>{t('image.describe.result.copy', { defaultValue: 'Copy Prompt' })}</span>
                  </>
                )}
              </button>
            </div>
            <div className="bg-white dark:bg-secondary-800 rounded-lg p-4 text-sm text-gray-800 dark:text-secondary-200 border border-gray-200 dark:border-secondary-600">
              {result.prompt}
            </div>
          </div>

          {/* Tags */}
          {Array.isArray(result.tags) && result.tags.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-secondary-100 mb-2">
                {t('image.describe.result.tags', { defaultValue: 'Tags' })}
              </h4>
              <div className="flex flex-wrap gap-2">
                {result.tags.map((tag, tagIndex) => (
                  <span
                    key={`tag-${tag}-${tagIndex}`}
                    className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Use Prompt Button */}
          {onUsePrompt && (
            <button onClick={handleUsePrompt} className="btn-primary-full">
              <span>
                {t('image.describe.result.usePrompt', { defaultValue: 'Use This Prompt' })}
              </span>
              <ArrowRight className="h-5 w-5" />
            </button>
          )}

          {/* Processing Time */}
          {result.processing_time_seconds && (
            <div className="text-xs text-gray-600 dark:text-secondary-400 text-center">
              {t('image.describe.result.processingTime', {
                defaultValue: 'Analyzed in {{time}} seconds',
                time: result.processing_time_seconds.toFixed(1),
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DescribeTab;
